/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

/**
 * Scroll spy direction options
 *
 * @typedef {Object} ScrollSpyOptions
 * @property {number|string|'auto'|((visible:{top:number,bottom:number,height:number}) => number)} [scanLine]
 *   Position of the invisible horizontal scan line used to determine the active section.
 *     - number: pixels measured from the top of the visible viewport (i.e. below the sticky header)
 *     - percent string (e.g. '50%'): percentage of the visible viewport height
 *     - 'auto': aligns with the top of the visible viewport (= scroll-padding-top)
 *     - function: receives the resolved visible viewport rect and returns the Y coordinate
 *   Default: '50%' (centered between sticky header and sticky footer).
 * @property {'center'|'edge'} [activeStrategy]
 *   How the closest section to the scan line is chosen when no section overlaps it (or to break
 *   ties between multiple overlapping sections).
 *     - 'center': minimum distance between the (clipped) section's vertical center and the line
 *     - 'edge':   minimum distance between the (clipped) section's nearest edge and the line
 *   Default: 'center'.
 * @property {boolean} [pinFirstAtTop]
 *   When the page is scrolled to the very top, force the first section as active regardless of
 *   the geometric pick. Avoids weird highlights on page load when small leading sections do not
 *   reach the configured scan line. Default: true.
 * @property {boolean} [pinLastAtBottom]
 *   When the page is scrolled to the very bottom, force the last section as active regardless of
 *   the geometric pick. Default: true.
 * @property {boolean} [oppositeGuard]
 *   Reject geometric picks that would move the active section in the opposite direction of the
 *   user's scroll. Acts as a defence-in-depth against backward jumps caused by mid-scroll layout
 *   shifts (the click-induced "small-section skip" case is primarily handled by `adaptiveScanLine`).
 *   Default: true.
 * @property {boolean} [adaptiveScanLine]
 *   When the user clicks a target whose section does not overlap the configured scan line (i.e.
 *   the section is too small / too high to reach it), temporarily anchor a virtual scan line at
 *   `Math.min(configured-line, clicked-section-center)` so neighbouring small sections become
 *   active sequentially as the user scrolls past them. Releases automatically when the adaptive
 *   line and the configured line resolve the same section (i.e. a section large enough to span
 *   both lines comes into view). Default: true.
 * @property {string} [updateUrl]
 *   When set to 'Push' or 'Replace', updates the URL hash on active section change using
 *   `history.pushState` or `history.replaceState` respectively. Default: undefined (no update).
 */
export const scrollSpyOptions = {
  default: {
    scanLine: '25%',
    activeStrategy: 'center',
    pinFirstAtTop: true,
    pinLastAtBottom: true,
    oppositeGuard: true,
    adaptiveScanLine: true
  }
};
